import React, { useState, useMemo, useCallback } from 'react'
import { useVehicles } from '../../../context/VehicleContext'
import FleetSummaryCards from './FleetSummaryCards'
import FleetSnapshotCards from './FleetSnapshotCards'
import FleetOemReport from './FleetOemReport'
import FleetSnapshotOemTable from './FleetSnapshotOemTable'
// import CommIgnOffReport from './CommIgnOffReport'
import CyberButton from './CyberButton'
import api from '../../../service/api'
import API_BOOK from '../../../service/endpoints'
// import InlineLoader from '../../helpers/hoc/loader/InlineLoader'

const COMM_KEYS = new Set(['Communicating', 'Communicating but Vehicle Off / No Network'])
const NON_COMM_KEY = 'Non-Communicating'

// const TABS = [
//   { id: 'fleet-status', label: 'Fleet Status Report' },
//   { id: 'comm-ign-off', label: 'Communicating · Ignition OFF' },
// ]

const SAMenu = () => {
  const { vehicles, loading, error, lastUpdated, countdown, load } = useVehicles()
  // const [activeTab, setActiveTab] = useState('fleet-status')
  const [cityFilter,        setCityFilter]        = useState('All cities')
  const [hubFilter,         setHubFilter]         = useState('All hubs')
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('All')
  const [statusFilter,      setStatusFilter]      = useState('All')

  // Historical snapshot state
  const [historyDate, setHistoryDate] = useState('')
  const [snapshots, setSnapshots]     = useState([])
  const [selectedSnap, setSelectedSnap] = useState(null)
  const [histLoading, setHistLoading] = useState(false)
  const isHistoryMode = selectedSnap !== null

  const fetchSnapshots = useCallback(async (date) => {
    if (!date) return
    setHistLoading(true)
    setSelectedSnap(null)
    try {
      const resp = await api({
        method: API_BOOK.REPORTS.FLEET_STATUS.GET_HISTORY.method,
        url: API_BOOK.REPORTS.FLEET_STATUS.GET_HISTORY.url,
        data: { fromDate: date, toDate: date }
      })
      const rows = resp?.data?.data?.rows || []
      setSnapshots(rows)
      if (rows.length > 0) setSelectedSnap(rows[rows.length - 1])
    } catch (err) {
      console.error('[FleetHistory]', err)
      setSnapshots([])
    }
    setHistLoading(false)
  }, [])

  const clearHistory = useCallback(() => {
    setHistoryDate('')
    setSnapshots([])
    setSelectedSnap(null)
  }, [])

  const cityOptions = useMemo(() => {
    const cities = Array.from(new Set(vehicles.map(v => v.mg?.city).filter(Boolean))).sort()
    return ['All cities', ...cities]
  }, [vehicles])

  const vehicleTypeOptions = useMemo(() => {
    const types = Array.from(new Set(vehicles.map(v => v.mg?.vehicle_type).filter(Boolean))).sort()
    return ['All', ...types]
  }, [vehicles])

  const hubOptions = useMemo(() => {
    const source = cityFilter !== 'All cities'
      ? vehicles.filter(v => v.mg?.city === cityFilter)
      : vehicles
    const hubs = Array.from(new Set(source.map(v => v.mg?.depot_name).filter(Boolean))).sort()
    return ['All hubs', ...hubs]
  }, [vehicles, cityFilter])

  const handleCityChange = (city) => {
    setCityFilter(city)
    setHubFilter('All hubs')
  }

  // city+hub+vehicleType filtered base — used by CommIgnOffReport (independent of status filter)
  const commIgnBase = useMemo(() => {
    let result = vehicles
    if (cityFilter !== 'All cities')  result = result.filter(v => v.mg?.city === cityFilter)
    if (hubFilter !== 'All hubs')     result = result.filter(v => v.mg?.depot_name === hubFilter)
    if (vehicleTypeFilter !== 'All')  result = result.filter(v => v.mg?.vehicle_type === vehicleTypeFilter)
    return result
  }, [vehicles, cityFilter, hubFilter, vehicleTypeFilter])

  const filtered = useMemo(() => {
    let result = commIgnBase
    if (statusFilter === 'Communicating')     result = result.filter(v => COMM_KEYS.has(v.status))
    if (statusFilter === 'Non-Communicating') result = result.filter(v => v.status === NON_COMM_KEY)
    if (statusFilter === 'No API')            result = result.filter(v => v.status === 'No API')
    if (statusFilter === '>12h Ignition Off') result = result.filter(v => v.status === 'More than 12 hours ignition off')
    return result
  }, [commIgnBase, statusFilter])

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.6)', fontSize: 15 }}>
        {/* <InlineLoader /> */}
        Loading fleet data…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: '#fca5a5', fontSize: 14 }}>
        {error}
        <CyberButton onClick={load} style={{ marginLeft: 12 }}>Retry</CyberButton>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px 24px', color: '#fff', minHeight: '100%' }}>

      {/* Header bar with history date picker */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>
            Fleet Dashboard
            {isHistoryMode && <span style={{ fontSize: 13, fontWeight: 500, color: '#a78bfa', marginLeft: 10 }}>Historical View</span>}
          </h1>
          {!isHistoryMode && lastUpdated && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.40)', marginTop: 3 }}>
              Last updated {lastUpdated.toLocaleTimeString()} · refresh in {countdown}s
            </div>
          )}
          {isHistoryMode && selectedSnap && (
            <div style={{ fontSize: 11, color: '#a78bfa', marginTop: 3 }}>
              Snapshot: {selectedSnap.date} at {selectedSnap.time?.slice(0, 5)} IST
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="date"
            value={historyDate}
            onChange={e => { setHistoryDate(e.target.value); fetchSnapshots(e.target.value) }}
            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #424666', background: '#1a1e35', color: '#fff', fontSize: 12, outline: 'none' }}
          />
          {snapshots.length > 0 && (
            <select
              value={selectedSnap ? `${selectedSnap.date}-${selectedSnap.time}` : ''}
              onChange={e => {
                const s = snapshots.find(s => `${s.date}-${s.time}` === e.target.value)
                if (s) setSelectedSnap(s)
              }}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #424666', background: '#1a1e35', color: '#fff', fontSize: 12, outline: 'none', minWidth: 130 }}
            >
              {snapshots.map(s => (
                <option key={`${s.date}-${s.time}`} value={`${s.date}-${s.time}`} style={{ background: '#1a1e35', color: '#fff' }}>
                  {s.time?.slice(0, 5)} IST
                </option>
              ))}
            </select>
          )}
          {historyDate && snapshots.length === 0 && !histLoading && (
            <span style={{ fontSize: 11, color: '#fca5a5' }}>No snapshots</span>
          )}
          {histLoading && <span style={{ fontSize: 11, color: '#a0a3bd' }}>Loading...</span>}
          {isHistoryMode && (
            <CyberButton onClick={clearHistory}>Back to Live</CyberButton>
          )}
        </div>
      </div>

      {/* Summary cards — live or historical */}
      {isHistoryMode
        ? <FleetSnapshotCards snapshot={selectedSnap} />
        : <FleetSummaryCards vehicles={filtered} />
      }

      {/* Tab navigation — uncomment when CommIgnOffReport is ready to ship
      <div style={{ display: 'flex', gap: 0, marginTop: 24, borderBottom: '2px solid #424666' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '9px 20px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #7c3aed' : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 700,
              color: activeTab === tab.id ? '#c4b5fd' : 'rgba(255,255,255,0.40)',
              letterSpacing: 0.2,
              transition: 'color 0.15s',
              outline: 'none',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      */}

      {/* Fleet Status Report — live or historical */}
      {isHistoryMode
        ? <FleetSnapshotOemTable snapshot={selectedSnap} />
        : <FleetOemReport
            vehicles={filtered}
            cityFilter={cityFilter} setCityFilter={handleCityChange} cityOptions={cityOptions}
            hubFilter={hubFilter}   setHubFilter={setHubFilter}       hubOptions={hubOptions}
            vehicleTypeFilter={vehicleTypeFilter} setVehicleTypeFilter={setVehicleTypeFilter} vehicleTypeOptions={vehicleTypeOptions}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          />
      }

      {/* Communicating · Ignition OFF report — uncomment when ready to ship
      {activeTab === 'comm-ign-off' && (
        <CommIgnOffReport
          vehicles={commIgnBase}
          cityFilter={cityFilter} setCityFilter={handleCityChange} cityOptions={cityOptions}
          hubFilter={hubFilter}   setHubFilter={setHubFilter}       hubOptions={hubOptions}
          vehicleTypeFilter={vehicleTypeFilter} setVehicleTypeFilter={setVehicleTypeFilter} vehicleTypeOptions={vehicleTypeOptions}
        />
      )}
      */}

    </div>
  )
}

export default SAMenu
